/**
 * useEasyNav — EasyNav Guided Navigation Composable
 *
 * Central composable for EasyNav persona detection, state management,
 * and per-page dismiss tracking. Delegates persistence to auth.store.
 */

import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth.store'
import { UserRole, type EasyNavPersona } from '@/types/auth.types'

export function useEasyNav() {
    const authStore = useAuthStore()

    // ── Reactive state from store ────────────────────────────────────────────

    const prefs = computed(() => authStore.easyNavPrefs)
    const isEnabled = computed(() => prefs.value.enabled)
    const persona = computed(() => authStore.activePersona)
    const savedPersona = computed(() => prefs.value.persona)
    const seenWelcome = computed(() => prefs.value.seen_welcome)
    const dismissedPages = computed(() => prefs.value.dismissed_pages ?? [])

    /**
     * Returns true if the user has never been shown the EasyNav welcome
     * and EasyNav is enabled — this is the auto-redirect trigger.
     */
    const shouldShowWelcome = computed(
        () => prefs.value.enabled && !prefs.value.seen_welcome
    )

    // ── Persona auto-detection ───────────────────────────────────────────────

    /**
     * Deduce the best persona from the user's profile roles.
     * Returns null if no confident deduction can be made (partner always manual).
     */
    const deducedPersona = computed((): EasyNavPersona | null => {
        const roles = authStore.userRoles
        if (roles.includes(UserRole.CHAPTER_LEAD) || roles.includes(UserRole.CURATOR)) {
            return 'organizer'
        }
        if (roles.includes(UserRole.MEMBER)) {
            return 'member'
        }
        // Admin and Partner have no auto-deduction
        return null
    })

    /**
     * Whether the current persona was auto-detected (vs manually set by user).
     * True when persona matches the deduced value and no manual selection has been made.
     */
    const isPersonaDeduced = computed((): boolean => {
        if (!deducedPersona.value || !persona.value) return false
        return persona.value === deducedPersona.value
    })

    // ── Per-page dismiss tracking ────────────────────────────────────────────

    /**
     * Returns true if the EasyNavPanel has been dismissed on the given route name.
     */
    function isDismissedOn(routeName: string | null | undefined): boolean {
        if (!routeName) return false
        return dismissedPages.value.includes(routeName)
    }

    /**
     * Whether the panel should be visible on the current page.
     * Must be called with the current route name.
     */
    function isPanelVisibleOn(routeName: string | null | undefined): boolean {
        return isEnabled.value && !isDismissedOn(routeName)
    }

    // ── Actions ──────────────────────────────────────────────────────────────

    /**
     * Set or change the active persona. Pass `manual = true` when the user
     * explicitly selects; this will override any deduced suggestion.
     */
    /**
     * Set or change the active persona. Pass `persist = false` by default when the user
     * selects from selection pane; if `persist = true`, it will save to DB.
     */
    async function setPersona(p: EasyNavPersona | null, persist = false) {
        authStore.setActivePersona(p)
        if (persist) {
            return authStore.updateEasyNavPrefs({ persona: p })
        }
    }

    /**
     * Enable or disable EasyNav globally.
     */
    async function toggle(enabled: boolean) {
        if (!enabled) {
            authStore.setActivePersona(null)
            return authStore.updateEasyNavPrefs({ enabled, persona: null })
        }
        return authStore.updateEasyNavPrefs({ enabled })
    }

    /**
     * Mark the welcome landing page as seen. Call after first EasyNav visit
     * to prevent the auto-redirect from firing on subsequent logins.
     */
    async function markWelcomeSeen() {
        return authStore.updateEasyNavPrefs({ seen_welcome: true })
    }

    /**
     * Dismiss the EasyNavPanel for a specific route. The panel will no longer
     * appear on that page, but remains visible everywhere else.
     */
    async function dismissOnPage(routeName: string) {
        const current = dismissedPages.value
        if (current.includes(routeName)) return
        return authStore.updateEasyNavPrefs({
            dismissed_pages: [...current, routeName],
        })
    }

    /**
     * Restore the EasyNavPanel on a previously dismissed page.
     */
    async function restoreOnPage(routeName: string) {
        const current = dismissedPages.value
        return authStore.updateEasyNavPrefs({
            dismissed_pages: current.filter((p) => p !== routeName),
        })
    }

    /**
     * Clear all EasyNav preferences and disable guidance.
     */
    async function resetAll() {
        authStore.setActivePersona(null)
        return authStore.updateEasyNavPrefs({
            enabled: false,
            persona: null,
            seen_welcome: false,
            dismissed_pages: [],
        })
    }

    /**
     * Initialize EasyNav for a brand-new user.
     * Detects persona automatically and enables guidance.
     */
    async function initForNewUser(adminDefaultEnabled = true) {
        return authStore.updateEasyNavPrefs({
            enabled: adminDefaultEnabled,
            persona: deducedPersona.value,
            seen_welcome: false,
            dismissed_pages: [],
        })
    }

    return {
        // State
        prefs,
        isEnabled,
        persona,
        savedPersona,
        seenWelcome,
        dismissedPages,
        shouldShowWelcome,
        deducedPersona,
        isPersonaDeduced,

        // Page-level helpers
        isDismissedOn,
        isPanelVisibleOn,

        // Actions
        setPersona,
        toggle,
        markWelcomeSeen,
        dismissOnPage,
        restoreOnPage,
        resetAll,
        initForNewUser,
    }
}
